import { useState, useEffect, useMemo, useRef, type SetStateAction } from 'react';
import { Field, FieldTextAlign, resolveFieldTextAlign } from './constants';
import modalRegistry from '../../modals';

export const useFieldManagement = (initialFields: Field[], groupEssentialFields: string[]) => {
  const [localFields, setLocalFields] = useState<Field[]>(initialFields);
  // Optional writer so modal save / delete can record undo without this hook owning history.
  const fieldWriterRef = useRef<((value: SetStateAction<Field[]>) => void) | null>(null);
  const bindFieldWriter = (writer: ((value: SetStateAction<Field[]>) => void) | null) => {
    fieldWriterRef.current = writer;
  };
  const writeFields = (value: SetStateAction<Field[]>) => {
    const writer = fieldWriterRef.current ?? setLocalFields;
    writer(value);
  };
  const [editingFieldIdx, setEditingFieldIdx] = useState<number | null>(null);
  const [bulkEditingIdxs, setBulkEditingIdxs] = useState<number[] | null>(null);
  const [modalFieldName, setModalFieldName] = useState<string>('');
  const [modalFieldType, setModalFieldType] = useState<string>('text');
  const [modalRequired, setModalRequired] = useState<boolean>(false);
  const [modalComponent, setModalComponent] = useState<string>('');
  const [modalOptionsInput, setModalOptionsInput] = useState<string>('');
  const [modalFontSize, setModalFontSize] = useState<number>(12);
  const [modalTextAlign, setModalTextAlign] = useState<FieldTextAlign>('left');
  const [duplicateFieldError, setDuplicateFieldError] = useState<string | null>(null);

  const getFieldTopLeft = (field: Field): { x: number; y: number } => {
    const p = field?.position;
    if (!p) return { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
    return {
      x: Math.min(p.x0, p.x1),
      y: Math.min(p.y0, p.y1),
    };
  };

  const sortIndicesByVisualPosition = (fields: Field[], indices: number[]): number[] => {
    const ROW_SNAP = 10;
    return [...indices].sort((a, b) => {
      const fa = fields[a];
      const fb = fields[b];
      const pa = getFieldTopLeft(fa);
      const pb = getFieldTopLeft(fb);

      const ra = Math.floor(pa.y / ROW_SNAP);
      const rb = Math.floor(pb.y / ROW_SNAP);
      if (ra !== rb) return ra - rb;

      if (pa.x !== pb.x) return pa.x - pb.x;
      if (pa.y !== pb.y) return pa.y - pb.y;
      return a - b;
    });
  };

  // Normalize any incoming name to: lowercase ascii, underscores only
  const normalizeName = (value: string): string => {
    const v = (value || '').toString();
    // Strip diacritics, lowercase, replace non [a-z0-9_] with underscore, collapse repeats and trim underscores
    return v
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/_{2,}/g, '_');
  };

  const optionsToMultilineInput = (opts: Field['options'], kind: 'select' | 'radio'): string => {
    if (!Array.isArray(opts)) return '';
    if (kind === 'radio') {
      return opts
        .map(o => {
          if (typeof o === 'string') return o;
          const value = (o?.value ?? '').toString().trim();
          const label = (o?.label ?? '').toString().trim();
          if (!value && !label) return '';
          return label && label !== value ? `${value}|${label}` : value;
        })
        .filter(Boolean)
        .join('\n');
    }
    // select
    return opts
      .map(o => (typeof o === 'string' ? o : (o?.value ?? o?.label ?? '').toString()))
      .map(s => s.trim())
      .filter(Boolean)
      .join('\n');
  };

  const parseRadioOptions = (input: string): { value: string; label: string }[] => {
    return (input || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const parts = line.split('|');
        const value = (parts[0] ?? '').trim();
        const label = (parts.slice(1).join('|') || value).trim();
        return { value, label };
      })
      .filter(o => !!o.value);
  };

  // Update fields when initialFields changes
  useEffect(() => {
    setLocalFields(initialFields);
  }, [initialFields]);

  // Update essential fields when groupEssentialFields changes
  useEffect(() => {
    const essentialSet = new Set((groupEssentialFields || [])
      .filter((f): f is string => typeof f === 'string')
      .map(f => f.trim().toLowerCase())
      .filter(Boolean)
    );
    
    setLocalFields(prevFields => {
      const updatedFields = prevFields.map(field => {
        if (!field.name) return field;
        const fieldName = field.name.trim().toLowerCase();
        const shouldBeRequired = essentialSet.has(fieldName);
        return field.required !== shouldBeRequired 
          ? { ...field, required: shouldBeRequired } 
          : field;
      });
      
      return updatedFields.some((field, i) => field.required !== prevFields[i]?.required) 
        ? updatedFields 
        : prevFields;
    });
  }, [groupEssentialFields]);

  // Check for duplicate field names
  const hasDuplicateFieldNames = useMemo(() => {
    const nameCounts = localFields.reduce((acc: Record<string, number>, f) => {
      acc[f.name] = (acc[f.name] || 0) + 1;
      return acc;
    }, {});
    return Object.values(nameCounts).some(count => count > 1);
  }, [localFields]);

  const duplicateNames = useMemo(() => {
    const counts: Record<string, number> = {};
    localFields.forEach(f => {
      const name = f.name && typeof f.name === 'string' ? f.name.trim().toLowerCase() : '';
      if (name) counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).filter(([_, count]) => count > 1).map(([name]) => name);
  }, [localFields]);

  const essentialFieldsSet = useMemo(() => 
    new Set((groupEssentialFields || []).map(f => 
      f && typeof f === 'string' ? f.trim().toLowerCase() : ''
    ).filter(Boolean)), 
    [groupEssentialFields]
  );

  const localFieldsWithRequired = useMemo(() => 
    localFields.map(field => {
      const isEssential = field.name && essentialFieldsSet.has(field.name.trim().toLowerCase());
      return isEssential ? { ...field, required: true } : field;
    }), 
    [localFields, essentialFieldsSet]
  );

  // Update duplicate field error
  useEffect(() => {
    if (hasDuplicateFieldNames) {
      setDuplicateFieldError('Duplicate field names detected. Please ensure all field names are unique.');
    } else {
      setDuplicateFieldError(null);
    }
  }, [hasDuplicateFieldNames]);

  // Modal options
  const modalOptions = [
    { value: '', label: 'No Modal' },
    ...modalRegistry.map(m => ({ value: m.name, label: m.name })),
    { value: 'InlineModal', label: 'Inline Modal (manual options)' },
    { value: 'CustomOptionsModal', label: 'Custom Options Modal' }
  ];

  const handleFieldNameIconClick = (idx: number) => {
    setBulkEditingIdxs(null);
    setEditingFieldIdx(idx);
    setModalFieldName(localFields[idx].name);
    setModalRequired(!!localFields[idx].required);
    setModalFieldType(localFields[idx].type);
    setModalComponent(localFields[idx].modalComponent || '');
    setModalOptionsInput(
      optionsToMultilineInput(
        localFields[idx].options,
        localFields[idx].type === 'radio' ? 'radio' : 'select'
      )
    );
    setModalFontSize(localFields[idx].fontsize || 12);
    setModalTextAlign(resolveFieldTextAlign(localFields[idx].textAlign));
  };

  const openBulkEdit = (indices: number[]) => {
    if (!indices || indices.length < 2) return;
    const sorted = sortIndicesByVisualPosition(localFields, indices);
    setEditingFieldIdx(null);
    setBulkEditingIdxs(sorted);
    // Seed modal values from the first field in selection
    const first = localFields[sorted[0]];
    if (first) {
      setModalFieldName(first.name || ''); // Name input hidden in bulk mode
      setModalRequired(!!first.required);
      setModalFieldType(first.type || 'text');
      setModalComponent(first.modalComponent || '');
      setModalOptionsInput(
        optionsToMultilineInput(
          first.options,
          first.type === 'radio' ? 'radio' : 'select'
        )
      );
      setModalFontSize(first.fontsize || 12);
      setModalTextAlign(resolveFieldTextAlign(first.textAlign));
    }
  };

  const handleModalOk = () => {
    const essentialSet = new Set((groupEssentialFields || []).map(f => 
      f && typeof f === 'string' ? f.trim().toLowerCase() : ''
    ).filter(Boolean));

    // Bulk edit path
    if (bulkEditingIdxs && bulkEditingIdxs.length >= 2) {
      const baseName = normalizeName((modalFieldName || '').trim());
      const sortedIdxs = [...bulkEditingIdxs];
      writeFields(prev => {
        // Build a set of existing names excluding selected indices to avoid collisions
        const existing = new Set(
          prev.map((f, i) => (sortedIdxs.includes(i) ? null : (f.name || '').trim().toLowerCase()))
              .filter((n): n is string => !!n)
        );
        // Precompute new names if a base name is provided, ensure uniqueness
        const nameMap: Record<number, string | undefined> = {};
        if (baseName) {
          const match = baseName.match(/^(.*)_(\d+)$/);
          const prefix = match?.[1] ? match[1] : baseName;
          const start = match?.[2] ? Number(match[2]) : 1;
          const startSuffix = Number.isFinite(start) && start > 0 ? start : 1;
          sortedIdxs.forEach((idx, pos) => {
            const initialSuffix = startSuffix + pos;
            let candidate = `${prefix}_${initialSuffix}`;
            let suffix = initialSuffix;
            while (existing.has(candidate.trim().toLowerCase())) {
              suffix += 1;
              candidate = `${prefix}_${suffix}`;
            }
            existing.add(candidate.trim().toLowerCase());
            nameMap[idx] = candidate;
          });
        }

        const updated = prev.map((f, i) => {
          if (!sortedIdxs.includes(i)) return f;
          const nextName = nameMap[i] ?? f.name; // keep original if no baseName provided
          const isEssentialNext = nextName && essentialSet.has(nextName.trim().toLowerCase());
          const base: Field = {
            ...f,
            name: nextName,
            type: modalFieldType,
            required: isEssentialNext ? true : modalRequired,
            fontsize: modalFontSize,
            textAlign: modalTextAlign,
            modalComponent: (modalFieldType === 'radio' || modalFieldType === 'dropdown') ? '' : modalComponent,
          };
          if (modalFieldType === 'dropdown') {
            return {
              ...base,
              options: modalOptionsInput.split(/\r?\n/).map(opt => opt.trim()).filter(Boolean),
            } as Field;
          }
          if (modalFieldType === 'select') {
            if (
              modalComponent === 'InlineModal' ||
              modalComponent === 'CustomOptionsModal' ||
              modalComponent === 'CustomManualModal' ||
              modalComponent === 'CustomSelectOptions'
            ) {
              return {
                ...base,
                options: modalOptionsInput.split(/\r?\n/).map(opt => opt.trim()).filter(Boolean)
              } as Field;
            } else {
              const registryEntry = modalRegistry.find(m => m.name === modalComponent);
              if (registryEntry && 'options' in registryEntry && Array.isArray((registryEntry as any).options)) {
                return { ...base, options: (registryEntry as any).options } as Field;
              }
              return base;
            }
          }
          if (modalFieldType === 'radio') {
            return {
              ...base,
              options: parseRadioOptions(modalOptionsInput),
            } as Field;
          }
          return base;
        });
        // reset state
        setBulkEditingIdxs(null);
        setModalFieldName('');
        setModalFieldType('text');
        setModalRequired(false);
        setModalComponent('');
        setModalOptionsInput('');
        return updated;
      });
      return;
    }

    // Single edit path
    if (editingFieldIdx === null) return;
    const trimmed = normalizeName(modalFieldName.trim());
    if (!trimmed) return;
    const isEssential = trimmed && essentialSet.has(trimmed.trim().toLowerCase());

    writeFields(prev => {
      const updated = prev.map((f, i) => {
        if (i === editingFieldIdx) {
          const base = { 
            ...f, 
            name: trimmed, 
            type: modalFieldType, 
            required: isEssential ? true : modalRequired, 
            fontsize: modalFontSize, 
            textAlign: modalTextAlign,
            modalComponent: (modalFieldType === 'radio' || modalFieldType === 'dropdown') ? '' : modalComponent 
          } as Field;
          if (modalFieldType === 'dropdown') {
            return {
              ...base,
              options: modalOptionsInput.split(/\r?\n/).map(opt => opt.trim()).filter(Boolean),
            } as Field;
          }
          if (modalFieldType === 'select') {
            if (
              modalComponent === 'InlineModal' ||
              modalComponent === 'CustomOptionsModal' ||
              modalComponent === 'CustomManualModal' ||
              modalComponent === 'CustomSelectOptions'
            ) {
              return { 
                ...base, 
                options: modalOptionsInput.split(/\r?\n/).map(opt => opt.trim()).filter(Boolean) 
              } as Field;
            } else {
              const registryEntry = modalRegistry.find(m => m.name === modalComponent);
              if (registryEntry && 'options' in registryEntry && Array.isArray((registryEntry as any).options)) {
                return { ...base, options: (registryEntry as any).options } as Field;
              }
              return base;
            }
          }
          if (modalFieldType === 'radio') {
            return {
              ...base,
              options: parseRadioOptions(modalOptionsInput),
            } as Field;
          }
          return base;
        }
        return f;
      });
      
      setEditingFieldIdx(null);
      setModalFieldName('');
      setModalFieldType('text');
      setModalRequired(false);
      setModalComponent('');
      setModalOptionsInput('');
      return updated;
    });
  };

  const handleDeleteField = (idx: number) => {
    writeFields(prev => prev.filter((_, i) => i !== idx));
    setEditingFieldIdx(null);
  };

  return {
    localFields,
    setLocalFields,
    bindFieldWriter,
    localFieldsWithRequired,
    editingFieldIdx,
    bulkEditingIdxs,
    modalFieldName,
    setModalFieldName,
    modalFieldType,
    modalRequired,
    modalComponent,
    modalOptionsInput,
    modalFontSize,
    modalTextAlign,
    modalOptions,
    duplicateFieldError,
    duplicateNames,
    hasDuplicateFieldNames,
    handleFieldNameIconClick,
    handleModalOk,
    handleModalCancel: () => { setEditingFieldIdx(null); setBulkEditingIdxs(null); },
    handleModalInputChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setModalFieldName(normalizeName(e.target.value));
    },
    handleModalRequiredChange: (e: React.ChangeEvent<HTMLInputElement>) => setModalRequired(e.target.checked),
    setModalFieldType,
    setModalComponent,
    setModalOptionsInput,
    setModalFontSize,
    setModalTextAlign,
    handleDeleteField,
    openBulkEdit,
  };
};